import { useEffect, useMemo, useState } from 'react'
import { useSearchParams } from 'react-router-dom'
import {
  AlertTriangle, Boxes, CheckCircle2, Download, FileSpreadsheet, FileText, IndianRupee, Pencil, ScrollText, Upload,
} from 'lucide-react'
import {
  Bar, BarChart, CartesianGrid, Cell, Pie, PieChart, ResponsiveContainer,
  Tooltip, XAxis, YAxis,
} from 'recharts'
import {
  Alert, Card, EmptyState, Field, Input, Loading, Modal, SearchInput, Select, Stat, Textarea, Th, sortRows, useSort, useToast,
} from '@/components/ui'
import { MaterialPicker } from '@/components/docs/shared'
import { ChartLegend } from '@/components/charts/ChartLegend'
import { useAuth } from '@/lib/auth'
import { DocumentDate, todayIso } from '@/components/docs/DocumentDate'
import { useMaterials, usePeople, useQuery, useSiteNames, useSites, useStock } from '@/lib/data'
import { supabase, callFunction, downloadFromFunction } from '@/lib/supabase'
import { can, seesManySites, visibleSites, weekLabel } from '@/lib/domain'
import { dateTime, ddmmyyyy, qty, rupees } from '@/lib/format'
import { enqueue } from '@/lib/sync'
import type { Material } from '@/lib/types'
import { CHART_SERIES } from '@/lib/theme'

const TABS = [
  { id: 'stock', label: 'Stock position', icon: Boxes },
  { id: 'ledger', label: 'Movement ledger', icon: ScrollText },
  { id: 'adjust', label: 'Adjustments', icon: Pencil },
  { id: 'opening', label: 'Opening stock', icon: Upload },
] as const

export function Inventory() {
  const { profile } = useAuth()
  const role = profile!.role
  const ho = seesManySites(role)
  // A Project Manager picking a colleague's site would see nothing, which
  // looks like a fault rather than a boundary. Offer him only his own.
  const [tab, setTab] = useState<typeof TABS[number]['id']>('stock')
  // ?site=...&q=... - arriving from "Where is this material?" on Master Data
  // (11-09-2026). Only a role that may pick a site honours ?site; the search
  // box is pre-filled with ?q. Without either, the screen opens as before.
  const [params] = useSearchParams()
  const [site, setSite] = useState(ho ? (params.get('site') ?? '') : (profile!.site_id ?? ''))
  // Lives here rather than in the tab because the Export button needs it too:
  // exporting a snapshot you are looking at should give you that snapshot.
  const [asAt, setAsAt] = useState('')
  const [exporting, setExporting] = useState(false)
  const toast = useToast()
  const { data: sites } = useSites()
  const mySites = useMemo(() => visibleSites(sites, role, profile!.id), [sites, role, profile])
  const historic = !!asAt && asAt < todayIso()

  // Opening stock is a one-time go-live job and sits with the Head of Stores,
  // the same person who owns manual adjustments.
  const visibleTabs = TABS.filter((t) =>
    (t.id !== 'adjust' && t.id !== 'opening') || can(role, 'adjustInventory'))

  return (
    <div className="space-y-4">
      <Card
        title="Inventory"
        subtitle={`Business week ${weekLabel()} — Saturday 00:00 to Friday 23:59 IST`}
        actions={
          // The valued stock position is a Head of Stores document. It puts a
          // rupee figure against every site, which is not a number to leave
          // lying around on a store-man's laptop.
          can(role, 'exportInventory') ? (
            <button className="btn-secondary btn-sm" disabled={exporting} onClick={async () => {
              // This used to be a bare call with no catch. When the server
              // returned an error the promise rejected into nothing and the
              // button appeared to do absolutely nothing — which is how a
              // broken export went unnoticed. Failures are now visible.
              setExporting(true)
              try {
                await downloadFromFunction('export-report', {
                  report: 'stock_position',
                  filters: {
                    ...(site ? { site_id: site } : {}),
                    ...(historic ? { as_at: asAt } : {}),
                  },
                }, historic
                  ? `stock_as_at_${asAt}${site ? '_' + site : ''}.xlsx`
                  : (site ? `stock_position_${site}.xlsx` : 'stock_position_all_sites.xlsx'))
                toast.success('Workbook downloaded.')
              } catch (e) {
                toast.error(e instanceof Error ? e.message : 'The export failed')
              } finally { setExporting(false) }
            }}>
              <Download className="h-4 w-4" />
              {exporting ? 'Preparing…'
                : historic ? `Export as at ${ddmmyyyy(asAt)}`
                : `Export${site ? ' this site' : ' all sites'}`}
            </button>
          ) : null
        }
      >
        <div className="flex flex-wrap items-center gap-2">
          <div className="flex flex-wrap gap-1 rounded-lg bg-slate-100 p-1">
            {visibleTabs.map((t) => (
              <button
                key={t.id}
                onClick={() => setTab(t.id)}
                className={`flex items-center gap-1.5 rounded-md px-3 py-1.5 text-xs font-semibold ${
                  tab === t.id ? 'bg-white text-oval-800 shadow-sm' : 'text-slate-600'}`}
              >
                <t.icon className="h-3.5 w-3.5" /> {t.label}
              </button>
            ))}
          </div>
          {ho && (
            <div className="w-full sm:w-64">
              <Select value={site} onChange={(e) => setSite(e.target.value)} placeholder="All sites"
                      options={mySites.map((s) => ({ value: s.id, label: s.site_name }))} />
            </div>
          )}
        </div>
      </Card>

      {tab === 'stock' && <StockTab siteId={site} setSiteId={setSite} sites={mySites} initialTerm={params.get('q') ?? ''}
                                    asAt={asAt} setAsAt={setAsAt} />}
      {tab === 'ledger' && <LedgerTab siteId={site} />}
      {tab === 'adjust' && <AdjustTab siteId={site || (profile!.site_id ?? 'HO')} />}
      {tab === 'opening' && <OpeningStockTab />}
    </div>
  )
}

// ---------------------------------------------------------------------------
/**
 * Stock position, live or as at any past date.
 *
 * Leaving the date blank shows the running balances, exactly as before.
 * Setting a past date switches to a point-in-time reconstruction summed from
 * the ledger — which is exact rather than estimated, because the ledger is
 * append-only and every movement carries the date it really happened.
 *
 * Two things appear only in the past view:
 *
 *   IN TRANSIT — stock despatched by that date but not yet received. It was at
 *   neither site, and without showing it the company total would look short.
 *
 *   VALUE AT THE COST THEN — priced at the last rate agreed on or before the
 *   snapshot, never one agreed afterwards.
 */
// ---------------------------------------------------------------------------
// What the board asked for on 03-09-2026: the working-capital picture, above
// the stock list
// ---------------------------------------------------------------------------
// Four questions a director actually asks about inventory, in order of how
// much money they represent:
//
//   1. HOW LONG HAS IT BEEN SITTING?  Stock that has not moved in six months is
//      cash buried in a shed. Nothing in the app said so before this.
//   2. WHERE IS IT PARKED?            Which sites are holding the money.
//   3. WHAT IS AT RISK OF STOPPING A JOB?  The below-minimum lines by what they
//      are worth, not merely counted. "91 lines low" tells nobody anything.
//   4. WHAT IS IT MADE OF?            Concentration by material category - if
//      two thirds is steel, a steel price move is a balance-sheet event.
//
// EVERYTHING IS COMPUTED FROM THE ROWS THE TABLE BELOW IS ABOUT TO RENDER.
// Not from a second query. That is deliberate: the charts, the running total
// and the list are the same array, so they cannot disagree with each other,
// and row level security has already decided what is in it. A store-man sees
// his own site's picture, which is exactly what is useful to him.
//
// Rupee figures follow maySeeValue, the same gate the table uses. Where a user
// may not see value, every chart silently switches to counting LINES instead,
// so the panel is still useful rather than blank.
// ---------------------------------------------------------------------------
// Branding 30-09-2026: the shared on-brand series (src/lib/theme.ts).
const INV_COLOURS = CHART_SERIES

function StockInsights({ rows, maySeeValue, historic, asAt, loading, filtered }: {
  rows: any[]; maySeeValue: boolean; historic: boolean
  asAt: string; loading: boolean; filtered: boolean
}) {
  // Value where the user is allowed it, line counts where not. One code path,
  // one measure, swapped in a single place.
  const byValue = maySeeValue
  const measure = (r: any) => byValue ? Number(r.approx_value || 0) : 1
  const label = byValue ? 'Value' : 'Lines'
  const fmt = (n: number) => byValue ? rupees(n) : `${n} line${n === 1 ? '' : 's'}`

  const total = useMemo(() => rows.reduce((a, r) => a + measure(r), 0), [rows, byValue])

  // ---- 1. ageing ---------------------------------------------------------
  const ageing = useMemo(() => {
    const now = Date.now()
    const buckets = [
      { name: 'Under 30 days', max: 30, fill: '#0F766E' },
      { name: '31 – 90 days', max: 90, fill: '#7CC6BA' },
      { name: '91 – 180 days', max: 180, fill: '#806A45' },
      { name: 'Over 180 days', max: Infinity, fill: '#C2414A' },
    ].map((b) => ({ ...b, v: 0, lines: 0 }))
    let unknown = 0
    for (const r of rows) {
      if (!r.last_txn_at) { unknown += 1; continue }
      const days = (now - new Date(r.last_txn_at).getTime()) / 86400000
      const b = buckets.find((x) => days <= x.max) ?? buckets[buckets.length - 1]
      b.v += measure(r); b.lines += 1
    }
    return { buckets, unknown }
  }, [rows, byValue])

  const stale = ageing.buckets[2].v + ageing.buckets[3].v
  const staleLines = ageing.buckets[2].lines + ageing.buckets[3].lines

  // ---- 2. by site --------------------------------------------------------
  const sites = useMemo(() => {
    const m = new Map<string, number>()
    for (const r of rows) m.set(r.site_name, (m.get(r.site_name) ?? 0) + measure(r))
    const all = [...m.entries()].map(([name, v]) => ({ name, v })).sort((a, b) => b.v - a.v)
    if (all.length <= 10) return { top: all, count: all.length }
    const rest = all.slice(10).reduce((a, x) => a + x.v, 0)
    return { top: [...all.slice(0, 10), { name: `Other ${all.length - 10} sites`, v: rest }],
             count: all.length }
  }, [rows, byValue])

  // ---- 3. what is at risk ------------------------------------------------
  const risk = useMemo(() => {
    const low = rows.filter((r: any) => r.below_minimum)
    const m = new Map<string, number>()
    for (const r of low) m.set(r.site_name, (m.get(r.site_name) ?? 0) + measure(r))
    return {
      lines: low.length,
      value: low.reduce((a, r) => a + measure(r), 0),
      bySite: [...m.entries()].map(([name, v]) => ({ name, v }))
        .sort((a, b) => b.v - a.v).slice(0, 8),
      nil: rows.filter((r: any) => Number(r.quantity_on_hand) <= 0).length,
    }
  }, [rows, byValue])

  // ---- 4. by category ----------------------------------------------------
  const cats = useMemo(() => {
    const m = new Map<string, number>()
    for (const r of rows) {
      const k = r.category || 'Uncategorised'
      m.set(k, (m.get(k) ?? 0) + measure(r))
    }
    return [...m.entries()].map(([name, v]) => ({ name, v })).sort((a, b) => b.v - a.v)
  }, [rows, byValue])

  const axis = (n: number) => {
    if (!byValue) return String(Math.round(n))
    const a = Math.abs(n)
    if (a >= 10000000) return `${(n / 10000000).toFixed(1)}Cr`
    if (a >= 100000) return `${(n / 100000).toFixed(1)}L`
    if (a >= 1000) return `${Math.round(n / 1000)}K`
    return String(Math.round(n))
  }
  const box = 'rounded-lg border border-slate-200 bg-white p-4'
  const pc = (n: number) => total > 0 ? `${((n / total) * 100).toFixed(1)}%` : '—'

  if (loading || rows.length === 0) return null

  return (
    <div className="mt-4 space-y-4">
      {historic && (
        <Alert tone="info">
          These charts show the <strong>live</strong> position. Ageing and shortage cannot be
          rebuilt for {ddmmyyyy(asAt)} — the list below is the {ddmmyyyy(asAt)} snapshot.
        </Alert>
      )}

      <div className="grid gap-4 lg:grid-cols-2">
        {/* 1 ---------------------------------------------------------- */}
        <div className={box}>
          <h3 className="text-sm font-semibold text-slate-800">How long it has been sitting</h3>
          <p className="mb-2 text-xs text-slate-500">
            {staleLines === 0
              ? 'Everything here has moved within the last three months.'
              : <><strong className="text-red-700">{fmt(stale)}</strong> across {staleLines} line
                  {staleLines === 1 ? '' : 's'} has not moved in over three months — {pc(stale)} of
                  what is on this screen. That is working capital standing still.</>}
            {ageing.unknown > 0 && ` ${ageing.unknown} line${ageing.unknown === 1 ? ' has' : 's have'} never moved and are not counted here.`}
          </p>
          <ResponsiveContainer width="100%" height={240}>
            <BarChart data={ageing.buckets} margin={{ top: 4, right: 8, left: 8, bottom: 4 }}>
              <CartesianGrid strokeDasharray="3 3" stroke="#DFE6E3" vertical={false} />
              <XAxis dataKey="name" tick={{ fontSize: 10 }} />
              <YAxis tick={{ fontSize: 11 }} tickFormatter={axis} />
              <Tooltip formatter={(v: any, _n: any, p: any) =>
                [fmt(Number(v)), `${p?.payload?.lines ?? 0} lines`]} />
              <Bar dataKey="v" name={label} radius={[3, 3, 0, 0]}>
                {ageing.buckets.map((b, i) => <Cell key={i} fill={b.fill} />)}
              </Bar>
            </BarChart>
          </ResponsiveContainer>
        </div>

        {/* 2 ---------------------------------------------------------- */}
        <div className={box}>
          <h3 className="text-sm font-semibold text-slate-800">
            Where the {byValue ? fmt(total) : 'stock'} is parked
          </h3>
          <p className="mb-2 text-xs text-slate-500">
            {sites.top.length === 0 ? 'Nothing to show.'
              : sites.count === 1 ? <>All of it is at {sites.top[0].name}.</>
              : <>{sites.top[0].name} holds {pc(sites.top[0].v)}, across {sites.count} sites
                  {sites.count > 10 ? ' — largest ten shown' : ''}.</>}
          </p>
          <ResponsiveContainer width="100%" height={280}>
            <BarChart data={sites.top} layout="vertical" margin={{ top: 4, right: 18, left: 8, bottom: 4 }}>
              <CartesianGrid strokeDasharray="3 3" stroke="#DFE6E3" horizontal={false} />
              <XAxis type="number" tick={{ fontSize: 11 }} tickFormatter={axis} />
              <YAxis type="category" dataKey="name" width={150} tick={{ fontSize: 10 }} />
              <Tooltip formatter={(v: any) => fmt(Number(v))} />
              <Bar dataKey="v" name={label} fill="#3C55A5" radius={[0, 3, 3, 0]} />
            </BarChart>
          </ResponsiveContainer>
        </div>

        {/* 3 ---------------------------------------------------------- */}
        <div className={box}>
          <h3 className="text-sm font-semibold text-slate-800">What could stop a job</h3>
          <p className="mb-2 text-xs text-slate-500">
            {risk.lines === 0
              ? 'Nothing is below its minimum level.'
              : <><strong className="text-red-700">{risk.lines} line{risk.lines === 1 ? '' : 's'}</strong>{' '}
                  below minimum{byValue && <> — {fmt(risk.value)} of stock</>}, at{' '}
                  {risk.bySite.length} site{risk.bySite.length === 1 ? '' : 's'}.
                  {risk.nil > 0 && <> {risk.nil} {risk.nil === 1 ? 'line is' : 'lines are'} at nil.</>}</>}
          </p>
          {risk.bySite.length === 0 ? (
            <div className="flex h-[240px] items-center justify-center text-sm text-emerald-700">
              <CheckCircle2 className="mr-2 h-5 w-5" /> Every line is at or above its minimum.
            </div>
          ) : (
            <ResponsiveContainer width="100%" height={240}>
              <BarChart data={risk.bySite} layout="vertical" margin={{ top: 4, right: 18, left: 8, bottom: 4 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="#DFE6E3" horizontal={false} />
                <XAxis type="number" tick={{ fontSize: 11 }} tickFormatter={axis} />
                <YAxis type="category" dataKey="name" width={150} tick={{ fontSize: 10 }} />
                <Tooltip formatter={(v: any) => fmt(Number(v))} />
                <Bar dataKey="v" name="Short" fill="#C2414A" radius={[0, 3, 3, 0]} />
              </BarChart>
            </ResponsiveContainer>
          )}
        </div>

        {/* 4 ---------------------------------------------------------- */}
        <div className={box}>
          <h3 className="text-sm font-semibold text-slate-800">What it is made of</h3>
          <p className="mb-2 text-xs text-slate-500">
            {cats.length === 0 ? 'Nothing to show.'
              : cats.length === 1 ? <>All of it is {cats[0].name}.</>
              : <>{cats[0].name} is {pc(cats[0].v)} of it, across {cats.length} categories.</>}
          </p>
          {/* The key goes UNDERNEATH (12-09-2026). Beside the doughnut it
              overlapped the drawing on a phone. */}
          <ResponsiveContainer width="100%" height={220}>
            <PieChart>
              <Pie data={cats} dataKey="v" nameKey="name" innerRadius="52%" outerRadius="86%"
                   paddingAngle={2}>
                {cats.map((_, i) => <Cell key={i} fill={INV_COLOURS[i % INV_COLOURS.length]} />)}
              </Pie>
              <Tooltip formatter={(v: any) => fmt(Number(v))} />
            </PieChart>
          </ResponsiveContainer>
          <ChartLegend items={cats.map((c: any, i: number) => ({
            name: c.name, color: INV_COLOURS[i % INV_COLOURS.length], value: fmt(Number(c.v)),
          }))} />
        </div>
      </div>

      <p className="text-xs text-slate-400">
        {filtered ? 'These charts cover the filtered rows only. ' : ''}
        Worked out from the same {rows.length} lines listed below, so the figures here and the
        total at the foot of the list are the same arithmetic.
        {!byValue && ' Rupee figures are limited to the Head of Stores, so these count lines instead.'}
      </p>
    </div>
  )
}

function StockTab({ siteId, setSiteId, sites, asAt, setAsAt, initialTerm = '' }: {
  initialTerm?: string
  siteId: string
  setSiteId: (v: string) => void
  sites: { id: string; site_name: string }[]
  asAt: string
  setAsAt: (v: string) => void
}) {
  const toast = useToast()
  const { profile } = useAuth()
  // Same rule as the valued export: quantities are operational and everyone
  // with site access sees them; rupee figures are Head of Stores only. The
  // database blanks them too — this just avoids rendering a misleading zero.
  const maySeeValue = can(profile!.role, 'exportInventory')
  const [term, setTerm] = useState(initialTerm)
  const [onlyLow, setOnlyLow] = useState(false)
  const [snap, setSnap] = useState<any[]>([])
  const [snapLoading, setSnapLoading] = useState(false)

  const historic = !!asAt && asAt < todayIso()
  // Both sources are fetched for EVERY site the user may see, and the site
  // filter is applied here rather than in the query. That is what lets the
  // strip at the foot of the list show a site's value AND the company total
  // beside it — you cannot compare against a figure you never fetched. Row
  // level security still decides what "every site" means, so a store-man
  // fetches only his own and his "company total" is his own total.
  const { data: live, loading: liveLoading, error, fromCache } = useStock(null)
  const { sort, toggle } = useSort('site_name', 'asc')

  useEffect(() => {
    if (!historic) { setSnap([]); return }
    let alive = true
    setSnapLoading(true)
    void (async () => {
      const { data, error: e } = await supabase.rpc('rpc_stock_as_at', {
        p_as_at: asAt, p_site_id: null,
      })
      if (!alive) return
      if (e) { toast.error(e.message); setSnap([]) } else setSnap(data ?? [])
      setSnapLoading(false)
    })()
    return () => { alive = false }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [asAt, historic])

  // One shape for the table whichever source it came from.
  //
  // The live branch used to blank approx_value and rate_source. v_stock_position
  // computes both, so throwing them away meant the live screen could show no
  // value at all — no total, and nothing for the insight charts to work with.
  // They are kept now; whether a rupee figure is RENDERED is still decided by
  // maySeeValue, exactly as before, so no one sees a figure they could not see
  // yesterday.
  const data = useMemo(() => historic
    ? snap.map((r: any) => ({ ...r, quantity_on_hand: Number(r.quantity), last_txn_at: r.last_movement_on }))
    : live.map((r: any) => ({ ...r, bucket: 'SITE' })),
    [historic, snap, live])

  const loading = historic ? snapLoading : liveLoading

  const rows = useMemo(() => {
    const t = term.trim().toLowerCase()
    return data.filter((s: any) =>
      (!siteId || s.site_id === siteId) &&
      (!onlyLow || s.below_minimum) &&
      (!t || s.item_name.toLowerCase().includes(t) || s.item_code.toLowerCase().includes(t) ||
        (s.category ?? '').toLowerCase().includes(t)))
  }, [data, siteId, term, onlyLow])

  const siteRows    = rows.filter((r: any) => r.bucket !== 'IN_TRANSIT')
  const transitRows = rows.filter((r: any) => r.bucket === 'IN_TRANSIT')
  const lowCount    = siteRows.filter((s: any) => s.below_minimum).length
  const transitQty  = transitRows.reduce((a: number, r: any) => a + Number(r.quantity_on_hand || 0), 0)
  const totalValue  = rows.reduce((a: number, r: any) => a + Number(r.approx_value || 0), 0)
  // The running total at the foot of the list counts the SAME rows the list
  // shows — site rows only. totalValue above includes anything in transit,
  // which is right for the company figure in the card but would not match a
  // line count of the table underneath it.
  const siteValue   = siteRows.reduce((a: number, r: any) => a + Number(r.approx_value || 0), 0)
  // Every site the user may see, ignoring the site filter but respecting the
  // as-at date — so a site's figure can be shown against the whole.
  const allSiteRows = data.filter((r: any) => r.bucket !== 'IN_TRANSIT')
  const companyValue = allSiteRows.reduce((a: number, r: any) => a + Number(r.approx_value || 0), 0)
  const siteName    = siteId ? (sites.find((s) => s.id === siteId)?.site_name ?? siteId) : ''

  return (
    <>
      <div className="grid grid-cols-2 gap-3 lg:grid-cols-4">
        <Stat label="Material lines" value={siteRows.length} sub={siteId ? 'At this site' : 'Across all sites'} />
        <Stat label="Below minimum" value={lowCount} tone={lowCount ? 'bad' : 'good'} />
        {historic
          ? <Stat label="In transit" value={qty(transitQty)} tone={transitQty ? 'warn' : 'good'}
                  sub={transitQty ? 'Despatched, not yet received' : 'Nothing on the road'} />
          : <Stat label="Nil stock" value={siteRows.filter((s: any) => s.quantity_on_hand <= 0).length} tone="good" />}
        {historic && maySeeValue
          ? <Stat label="Approx value" value={rupees(totalValue)}
                  sub="At the cost in force then" icon={<IndianRupee className="h-5 w-5" />} />
          : historic
            ? <Stat label="Lines in transit" value={transitRows.length} />
            : <Stat label="Data" value={fromCache ? 'Cached' : 'Live'} tone={fromCache ? 'warn' : 'good'}
                    sub={fromCache ? 'Shown from this device while offline' : 'Fetched from the server'} />}
      </div>

      <StockInsights rows={siteRows} maySeeValue={maySeeValue} historic={historic}
                     asAt={asAt} loading={loading} filtered={!!term.trim() || onlyLow} />

      <Card
        className="mt-4"
        title={historic ? `Stock position as at ${ddmmyyyy(asAt)}` : 'Stock position'}
        subtitle={historic
          ? 'Close of business on that date, summed from the ledger'
          : undefined}
        actions={
          <label className="flex cursor-pointer items-center gap-2 text-xs font-medium text-slate-600">
            <input type="checkbox" className="h-4 w-4 rounded border-slate-300 text-oval-600"
                   checked={onlyLow} onChange={(e) => setOnlyLow(e.target.checked)} />
            Only below minimum
          </label>
        }
      >
        <div className="mb-3 grid gap-3 sm:grid-cols-3">
          <DocumentDate
            label="As at date"
            value={asAt}
            onChange={setAsAt}
            required={false}
            hint="Leave blank for today's live position."
          />
          {/* The same selection as the dropdown beside the tabs — change either
              and both move. One site is ever in force, so there is never a
              question of which one is winning. */}
          <Field label="Site" hint="Also changes the picker at the top of the page.">
            <Select value={siteId} onChange={(e) => setSiteId(e.target.value)}
                    placeholder="All sites"
                    options={sites.map((s) => ({ value: s.id, label: s.site_name }))} />
          </Field>
          <div>
            <SearchInput value={term} onChange={setTerm}
                         placeholder="Search material, code or category…" className="mt-6" />
          </div>
        </div>

        {asAt && !historic && (
          <div className="mb-3">
            <Alert tone="info" title="That is today">
              Showing the live position. Pick an earlier date to reconstruct a past one.
            </Alert>
          </div>
        )}

        {historic && (
          <div className="mb-3">
            <Alert tone="info" title="How this figure was arrived at">
              Every movement dated <strong>{ddmmyyyy(asAt)}</strong> or earlier, added up. The stock
              ledger cannot be edited or deleted, so this is arithmetic over an unalterable record
              rather than an estimate — including anything back-dated into that period since.
              Value uses the last rate agreed <strong>on or before</strong> that date, never one
              agreed afterwards.
              <p className="mt-2">
                &ldquo;Dated&rdquo; means the <strong>posting date</strong>, not the date on the
                challan. A receipt for material that arrived in a month which had already been
                closed posts to the month it was entered — so it appears from that later date,
                not from the day the lorry came. That is what keeps this figure and the accounts
                telling the same story.
              </p>
            </Alert>
          </div>
        )}

        {loading && <Loading />}
        {error && !historic && <EmptyState title="Could not load stock" message={error} />}
        {!loading && rows.length === 0 && (
          <EmptyState icon={<Boxes className="h-9 w-9" />}
                      title={historic ? `Nothing was in stock on ${ddmmyyyy(asAt)}` : 'No stock lines match'}
                      message={historic ? 'No movement had been posted at or before that date.' : undefined} />
        )}

        {!loading && siteRows.length > 0 && (
          <div className="table-wrap">
            <table className="tbl">
              <thead>
                <tr>
                  <Th id="site_name" sort={sort} onSort={toggle}>Site</Th><Th id="item_name" sort={sort} onSort={toggle}>Material</Th><Th id="item_code" sort={sort} onSort={toggle}>Code</Th><Th id="category" sort={sort} onSort={toggle}>Category</Th>
                  <Th id="quantity_on_hand" sort={sort} onSort={toggle} className="num" align="right">On hand</Th><Th id="min_stock_level" sort={sort} onSort={toggle} className="num" align="right">Min</Th><Th id="max_stock_level" sort={sort} onSort={toggle} className="num" align="right">Max</Th>
                  {maySeeValue && <Th id="approx_value" sort={sort} onSort={toggle} className="num" align="right">{historic ? 'Value then' : 'Approx value'}</Th>}
                  {maySeeValue && <Th id="rate_source" sort={sort} onSort={toggle}>Rate basis</Th>}
                  <Th id="last_txn_at" sort={sort} onSort={toggle}>Last movement</Th>
                </tr>
              </thead>
              <tbody>
                {sortRows(siteRows, sort, { quantity_on_hand: 'number', min_stock_level: 'number', max_stock_level: 'number', approx_value: 'number', last_txn_at: 'date' }).map((s: any) => (
                  <tr key={`${s.site_id}-${s.material_id}`} className={s.below_minimum ? 'bg-red-50/50' : ''}>
                    <td className="text-xs">{s.site_name}</td>
                    <td className="text-sm font-medium">{s.item_name}</td>
                    <td className="font-mono text-xs text-slate-500">{s.item_code}</td>
                    <td className="text-xs text-slate-500">{s.category ?? '—'}</td>
                    <td className={`num font-semibold ${s.below_minimum ? 'text-red-600' : 'text-slate-800'}`}>
                      {qty(s.quantity_on_hand)} <span className="text-xs font-normal text-slate-400">{s.uom}</span>
                      {s.below_minimum && <AlertTriangle className="ml-1 inline h-3.5 w-3.5 text-red-500" />}
                    </td>
                    <td className="num text-xs text-slate-500">{qty(s.min_stock_level)}</td>
                    <td className="num text-xs text-slate-500">{qty(s.max_stock_level)}</td>
                    {maySeeValue && (
                      <td className="num text-xs">
                        {s.approx_value == null ? '—' : rupees(s.approx_value)}
                      </td>
                    )}
                    {maySeeValue && (
                      <td className={`text-xs ${s.rate_source === 'No rate known by that date' ? 'text-red-600' : 'text-slate-500'}`}>
                        {s.rate_source}
                      </td>
                    )}
                    <td className="text-xs text-slate-500">
                      {s.last_txn_at ? (historic ? ddmmyyyy(s.last_txn_at) : dateTime(s.last_txn_at)) : '—'}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        {/* The running total. Quantity is deliberately NOT summed: bags, CFT,
            metres and pieces are all in this column, and adding them together
            would produce a number that means nothing. Lines and rupees are the
            two things that can honestly be totalled here. */}
        {!loading && siteRows.length > 0 && (
          <div className="sticky bottom-[calc(3.5rem+env(safe-area-inset-bottom))] lg:bottom-0 -mx-4 mt-0 border-t-2 border-oval-800 bg-oval-50 sm:-mx-6">
            <div className="flex flex-wrap items-center justify-between gap-x-8 gap-y-1 px-4 py-2.5 sm:px-6">
              <div className="text-xs font-semibold uppercase tracking-wide text-oval-800">
                {siteId ? siteName : 'Total'} · {siteRows.length} material
                line{siteRows.length === 1 ? '' : 's'}
                {lowCount > 0 && <span className="ml-2 font-normal text-red-600">
                  {lowCount} below minimum
                </span>}
              </div>
              <div className="flex flex-wrap items-baseline gap-x-8 gap-y-1 text-sm">
                {maySeeValue ? (
                  <span className="text-slate-600">
                    {historic ? `Value as at ${ddmmyyyy(asAt)}` : 'Approximate value'}{' '}
                    <strong className="text-slate-900">{rupees(siteValue)}</strong>
                    {/* The part against the whole, so a site figure is never
                        read out of proportion. */}
                    {siteId && companyValue > 0 && (
                      <span className="ml-2 text-xs text-slate-500">
                        {((siteValue / companyValue) * 100).toFixed(1)}% of {rupees(companyValue)}
                        {' '}across all {allSiteRows.length ? new Set(allSiteRows.map((r: any) => r.site_id)).size : 0} sites
                      </span>
                    )}
                  </span>
                ) : (
                  <span className="text-xs text-slate-500">
                    Rupee totals are limited to the Head of Stores.
                  </span>
                )}
                <span className="text-xs text-slate-500">
                  Quantities are not added — this column mixes bags, CFT, metres and pieces.
                </span>
              </div>
            </div>
          </div>
        )}

        {historic && transitRows.length > 0 && (
          <div className="mt-6">
            <h3 className="text-sm font-bold text-slate-800">
              On the road — {qty(transitQty)} units in transit
            </h3>
            <p className="mb-2 mt-0.5 text-xs text-slate-500">
              Despatched on or before {ddmmyyyy(asAt)} but not yet received, so it was at neither
              site. Counted here so the company total still reconciles.
            </p>
            <div className="table-wrap">
              <table className="tbl">
                <thead>
                  <tr><th>Transfer</th><th>From</th><th>To</th><th>Material</th>
                      <th className="num">Quantity</th>
                      {maySeeValue && <th className="num">Value then</th>}
                      <th>Despatched</th><th className="num">Days out</th></tr>
                </thead>
                <tbody>
                  {transitRows.map((r: any) => (
                    <tr key={`${r.transit_ref}-${r.material_id}`}
                        className={Number(r.days_in_transit) > 14 ? 'bg-amber-50/60' : ''}>
                      <td className="font-mono text-xs">{r.transit_ref}</td>
                      <td className="text-xs">{r.site_name}</td>
                      <td className="text-xs">{r.transit_to_site ?? '—'}</td>
                      <td className="text-sm font-medium">{r.item_name}</td>
                      <td className="num font-semibold">
                        {qty(r.quantity_on_hand)} <span className="text-xs font-normal text-slate-400">{r.uom}</span>
                      </td>
                      {maySeeValue && (
                        <td className="num text-xs">
                          {r.approx_value == null ? '—' : rupees(r.approx_value)}
                        </td>
                      )}
                      <td className="text-xs">{ddmmyyyy(r.last_txn_at)}</td>
                      <td className={`num text-xs font-semibold ${Number(r.days_in_transit) > 14 ? 'text-amber-700' : 'text-slate-500'}`}>
                        {r.days_in_transit}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            {transitRows.some((r: any) => Number(r.days_in_transit) > 14) && (
              <div className="mt-3">
                <Alert tone="warning" title="Some consignments had been out a long time">
                  Anything shaded amber had been in transit more than a fortnight on that date.
                  Usually it means somebody forgot to mark the transfer delivered.
                </Alert>
              </div>
            )}
          </div>
        )}
      </Card>
    </>
  )
}

// ---------------------------------------------------------------------------
const TXN_TONE: Record<string, string> = {
  GRN_IN: 'pill-green', MRN_IN: 'pill-green', IST_IN: 'pill-blue',
  MIN_OUT: 'pill-amber', IST_OUT: 'pill-blue', ADJUSTMENT: 'pill-violet', OPENING: 'pill-grey',
}

function LedgerTab({ siteId }: { siteId: string }) {
  // Names, not email addresses — management 01-09-2026.
  const people = usePeople()
  const [type, setType] = useState('')
  const [term, setTerm] = useState('')

  const ledgerSort = useSort('txn_date')
  const { sort, toggle } = ledgerSort
  const { data, loading, error } = useQuery<any>('inventory_ledger', () => {
    let q = supabase.from('v_inventory_ledger').select('*')
    if (siteId) q = q.eq('site_id', siteId)
    if (type) q = q.eq('txn_type', type)
    return q.order('created_at', { ascending: false }).limit(1000)
  }, [siteId, type], { cacheable: false })

  const rows = useMemo(() => {
    const t = term.trim().toLowerCase()
    if (!t) return data
    // Same rule as the adjustments table above: the Posted by cell shows a
    // name, so the name is what the column sorts on and what the box searches.
    const named = data.map((r: any) => ({ ...r, posted_by: people.name({ email: r.posted_by_email }) }))
    if (!t) return named
    return named.filter((r: any) =>
      (r.item_name ?? '').toLowerCase().includes(t) ||
      (r.ref_doc_id ?? '').toLowerCase().includes(t) ||
      r.posted_by.toLowerCase().includes(t))
  }, [data, term])

  return (
    <Card
      title="Movement ledger"
      subtitle="Append-only. Nothing is ever edited or deleted — corrections are posted as adjustments."
      actions={
        <button className="btn-secondary btn-sm" onClick={() =>
          downloadFromFunction('export-report',
            { report: 'inventory_ledger', filters: siteId ? { site_id: siteId } : {} }, 'inventory_ledger.xlsx')}>
          <Download className="h-4 w-4" /> Export
        </button>
      }
    >
      <div className="mb-3 grid gap-2 sm:grid-cols-2 lg:grid-cols-3">
        <SearchInput value={term} onChange={setTerm} placeholder="Search material or document…" />
        <Select value={type} onChange={(e) => setType(e.target.value)} placeholder="All movement types"
                options={[
                  { value: 'GRN_IN', label: 'Goods received (GRN)' },
                  { value: 'MIN_OUT', label: 'Issued to site (MIN)' },
                  { value: 'MRN_IN', label: 'Returned from site (MRN)' },
                  { value: 'IST_OUT', label: 'Transferred out (IST)' },
                  { value: 'IST_IN', label: 'Transferred in (IST)' },
                  { value: 'ADJUSTMENT', label: 'Manual adjustment' },
                ]} />
      </div>

      {loading && <Loading />}
      {error && <EmptyState title="Could not load the ledger" message={error} />}
      {!loading && rows.length === 0 && (
        <EmptyState icon={<ScrollText className="h-9 w-9" />} title="No movements recorded yet"
                    message="Stock movements appear here the moment the Head of Stores approves a GRN, MIN or MRN." />
      )}
      {rows.length > 0 && (
        <div className="table-wrap">
          <table className="tbl">
            <thead>
              <tr>
                <Th id="txn_date" sort={sort} onSort={toggle}>Date</Th><Th id="week_start" sort={sort} onSort={toggle}>Week from</Th><Th id="site_name" sort={sort} onSort={toggle}>Site</Th><Th id="item_name" sort={sort} onSort={toggle}>Material</Th>
                <Th id="txn_type" sort={sort} onSort={toggle}>Type</Th><Th id="quantity_delta" sort={sort} onSort={toggle} className="num" align="right">Quantity</Th><Th id="ref_doc_id" sort={sort} onSort={toggle}>Document</Th><Th id="posted_by" sort={sort} onSort={toggle}>Posted by</Th><Th id="remarks" sort={sort} onSort={toggle}>Remarks</Th>
              </tr>
            </thead>
            <tbody>
              {sortRows(rows, ledgerSort.sort, { txn_date: 'date', week_start: 'date', quantity_delta: 'number' }).map((r: any) => (
                <tr key={r.id}>
                  <td className="text-xs">{r.txn_date}</td>
                  <td className="text-xs text-slate-400">{r.week_start}</td>
                  <td className="text-xs">{r.site_name}</td>
                  <td className="text-sm">{r.item_name}</td>
                  <td><span className={TXN_TONE[r.txn_type] ?? 'pill-grey'}>{r.txn_type.replace('_', ' ')}</span></td>
                  <td className={`num font-semibold ${Number(r.quantity_delta) >= 0 ? 'text-emerald-700' : 'text-red-600'}`}>
                    {Number(r.quantity_delta) >= 0 ? '+' : ''}{qty(r.quantity_delta)} <span className="text-xs font-normal text-slate-400">{r.uom}</span>
                  </td>
                  <td className="font-mono text-xs text-slate-500">{r.ref_doc_id ?? '—'}</td>
                  <td className="max-w-[170px] truncate text-xs text-slate-500">{r.posted_by}</td>
                  <td className="max-w-[200px] truncate text-xs text-slate-500">{r.remarks ?? '—'}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </Card>
  )
}

// ---------------------------------------------------------------------------
const REASONS = [
  'Physical verification variance',
  'Damage / breakage at site',
  'Theft or loss',
  'Opening balance load',
  'Data entry correction',
  'Wastage beyond norm',
  'Other',
]

function AdjustTab({ siteId }: { siteId: string }) {
  const { profile } = useAuth()
  // Names, not email addresses — management 01-09-2026.
  const people = usePeople()
  const toast = useToast()
  const [open, setOpen] = useState(false)
  const { data: sites } = useSites()
  // Site names, not SITE-TRP-05 — 05-09-2026.
  const { siteName } = useSiteNames()
  const { data: materials } = useMaterials()
  const { data: stock } = useStock(siteId)

  const adjSort = useSort('created_at')
  const { sort, toggle } = adjSort
  const { data: history, loading, refresh } = useQuery<any>('inventory_adjustments', () =>
    supabase.from('inventory_adjustments').select('*').order('created_at', { ascending: false }).limit(200),
    [], { cacheable: false })

  const byId = useMemo(
    () => Object.fromEntries(materials.map((m) => [m.id, m])) as Record<string, Material>, [materials])

  // A column must sort by what the reader can actually see. The Material cell
  // shows the material's name while the row only carries its id, and the Reason
  // cell shows "CODE: free text" — so both are worked out once here and sorted
  // on. Sorting Material by material_id would look broken: the screen would
  // reorder by a code that is nowhere on it.
  // The By cell is the third of these: it shows a person's NAME while the row
  // carries an email address, so the name is worked out here and sorted on.
  const adjRows = useMemo(() => (history as any[]).map((a) => ({
    ...a,
    material_name: byId[a.material_id]?.item_name ?? a.material_id,
    reason_full: `${a.reason_code ?? ''}: ${a.reason ?? ''}`,
    adjusted_by: people.name({ email: a.adjuster_email }),
  })), [history, byId, people.index])

  const [site, setSite] = useState(siteId)
  const [material, setMaterial] = useState<Material | null>(null)
  const [delta, setDelta] = useState('')
  const [adjDate, setAdjDate] = useState(todayIso)
  const [reasonCode, setReasonCode] = useState(REASONS[0])
  const [reason, setReason] = useState('')
  const [busy, setBusy] = useState(false)
  const [err, setErr] = useState<string | null>(null)

  /*
   * The balance ON THE ADJUSTMENT DATE, not today.
   *
   * "Now 0, After 0" is worse than useless on a back-dated correction — it
   * describes a different day from the one being corrected. This reads the
   * ledger as at the chosen date, and falls back to the live figure while that
   * lookup is in flight or when the date is today.
   */
  const [asAtQty, setAsAtQty] = useState<number | null>(null)
  const liveQty = material ? (stock.find((s) => s.material_id === material.id)?.quantity_on_hand ?? 0) : 0
  const backDated = adjDate < todayIso()

  useEffect(() => {
    if (!material || !site || !backDated) { setAsAtQty(null); return }
    let alive = true
    void (async () => {
      const { data } = await supabase.rpc('fn_stock_on_hand_as_at', {
        p_site_id: site, p_material_id: material.id, p_as_at: adjDate,
      })
      if (alive) setAsAtQty(Number(data ?? 0))
    })()
    return () => { alive = false }
  }, [material, site, adjDate, backDated])

  const onHand = backDated && asAtQty !== null ? asAtQty : liveQty
  const after = onHand + (Number(delta) || 0)

  async function save() {
    if (!material) { setErr('Choose the material.'); return }
    if (!Number(delta)) { setErr('Enter a non-zero adjustment. Use a minus sign to reduce stock.'); return }
    if (after < 0) { setErr('That adjustment would drive the balance negative.'); return }
    if (reason.trim().length < 5) { setErr('Explain the adjustment — this is a permanent audit record.'); return }
    setErr(null); setBusy(true)
    try {
      await enqueue({
        kind: 'adjustment.create', docType: 'ADJ',
        label: `Adjust ${material.item_name} at ${site} by ${delta}`,
        payload: {
          site_id: site,
          material_id: material.id,
          quantity_delta: Number(delta),
          uom: material.unit_of_measure,
          reason_code: reasonCode,
          reason,
          adjustment_date: adjDate,
          adjusted_by: profile!.id,
          adjuster_email: profile!.email,
          balance_before: onHand,
          balance_after: after,
        },
      })
      toast.success('Adjustment posted to the ledger with a full audit trail.')
      setOpen(false); setMaterial(null); setDelta(''); setReason('')
      refresh()
    } catch (e) {
      setErr(e instanceof Error ? e.message : 'Could not post the adjustment')
    } finally { setBusy(false) }
  }

  return (
    <Card
      title="Manual inventory adjustments"
      subtitle="Head of Stores only. Every adjustment is permanently audit-trailed."
      actions={<button className="btn-primary btn-sm" onClick={() => { setSite(siteId); setOpen(true) }}>
        <Pencil className="h-4 w-4" /> New adjustment
      </button>}
    >
      <Alert tone="warning" title="Use sparingly">
        Stock should move through approved GRN, MIN, MRN and IST documents. An adjustment is for genuine
        exceptions — physical verification variance, damage, or loading an opening balance.
      </Alert>

      <div className="mt-4">
        {loading && <Loading />}
        {!loading && history.length === 0 && <p className="py-4 text-center text-sm text-slate-500">No adjustments have been made.</p>}
        {history.length > 0 && (
          <div className="table-wrap">
            <table className="tbl">
              <thead>
                <tr><Th id="id" sort={sort} onSort={toggle}>Reference</Th><Th id="created_at" sort={sort} onSort={toggle}>Date</Th><Th id="site_id" sort={sort} onSort={toggle}>Site</Th><Th id="material_name" sort={sort} onSort={toggle}>Material</Th>
                    <Th id="quantity_delta" sort={sort} onSort={toggle} className="num" align="right">Change</Th><Th id="balance_before" sort={sort} onSort={toggle} className="num" align="right">Before</Th><Th id="balance_after" sort={sort} onSort={toggle} className="num" align="right">After</Th>
                    <Th id="reason_full" sort={sort} onSort={toggle}>Reason</Th><Th id="adjusted_by" sort={sort} onSort={toggle}>By</Th></tr>
              </thead>
              <tbody>
                {sortRows(adjRows, adjSort.sort, { created_at: 'date', quantity_delta: 'number', balance_before: 'number', balance_after: 'number' }).map((a: any) => (
                  <tr key={a.id}>
                    <td className="font-mono text-xs">{a.id}</td>
                    <td className="text-xs">{dateTime(a.created_at)}</td>
                    <td className="text-xs" title={a.site_id}>{siteName(a.site_id)}</td>
                    <td className="text-sm">{a.material_name}</td>
                    <td className={`num font-semibold ${Number(a.quantity_delta) >= 0 ? 'text-emerald-700' : 'text-red-600'}`}>
                      {Number(a.quantity_delta) >= 0 ? '+' : ''}{qty(a.quantity_delta)}
                    </td>
                    <td className="num text-xs text-slate-500">{qty(a.balance_before)}</td>
                    <td className="num text-xs">{qty(a.balance_after)}</td>
                    <td className="max-w-[240px] truncate text-xs">{a.reason_code}: {a.reason}</td>
                    <td className="max-w-[160px] truncate text-xs text-slate-500">{a.adjusted_by}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      <Modal
        open={open} onClose={() => setOpen(false)}
        title="Adjust the inventory ledger"
        subtitle="A signed correction, recorded against your name"
        footer={<>
          <button className="btn-secondary" onClick={() => setOpen(false)} disabled={busy}>Cancel</button>
          <button className="btn-primary" onClick={save} disabled={busy}>Post adjustment</button>
        </>}
      >
        <div className="space-y-4">
          {err && <Alert tone="error">{err}</Alert>}
          <Field label="Site" required>
            <Select value={site} onChange={(e) => setSite(e.target.value)}
                    options={sites.map((s) => ({ value: s.id, label: s.site_name }))} />
          </Field>
          <Field label="Material" required>
            <MaterialPicker value={material?.id ?? null} onChange={setMaterial} />
          </Field>

          {/*
            An adjustment corrects the ledger on the day the discrepancy was
            found, which is rarely the day somebody gets to a keyboard. Without
            this the correction landed on today's date and the week it actually
            belonged to stayed wrong.
          */}
          <DocumentDate
            label="Adjustment date"
            value={adjDate}
            onChange={setAdjDate}
            hint="The day the count or the damage was found. Stock moves on this date."
          />

          {material && (
            <div className="grid grid-cols-3 gap-3 rounded-lg bg-slate-50 p-3 text-center text-sm">
              <div>
                <p className="text-xs text-slate-500">
                  {adjDate < todayIso() ? `On ${ddmmyyyy(adjDate)}` : 'Now'}
                </p>
                <p className="font-semibold">{qty(onHand)}</p>
              </div>
              <div><p className="text-xs text-slate-500">Change</p>
                   <p className={`font-semibold ${Number(delta) >= 0 ? 'text-emerald-700' : 'text-red-600'}`}>
                     {Number(delta) >= 0 ? '+' : ''}{delta || 0}</p></div>
              <div><p className="text-xs text-slate-500">After</p>
                   <p className={`font-semibold ${after < 0 ? 'text-red-600' : ''}`}>{qty(after)}</p></div>
            </div>
          )}
          <Field label="Adjustment quantity" required hint="Use a minus sign to reduce stock, e.g. -12">
            <Input type="number" inputMode="decimal" step="any" value={delta} onChange={(e) => setDelta(e.target.value)} />
          </Field>
          <Field label="Reason code" required>
            <Select value={reasonCode} onChange={(e) => setReasonCode(e.target.value)}
                    options={REASONS.map((r) => ({ value: r, label: r }))} />
          </Field>
          <Field label="Explanation" required hint="Permanently stored in the audit trail">
            <Textarea rows={3} value={reason} onChange={(e) => setReason(e.target.value)}
                      placeholder="e.g. Physical verification on 02-08-2026 found 12 bags short; godown leak damaged the stack." />
          </Field>
        </div>
      </Modal>
    </Card>
  )
}

// ---------------------------------------------------------------------------
/**
 * Loading the company's physical stock into the system at go-live.
 *
 * Every site counts what is on its shelves onto the company template. This
 * screen takes those files one at a time and turns each row into an OPENING
 * movement, which is what makes the digital stock position match the real one
 * on day one.
 *
 * Two safeguards, both deliberate:
 *
 *   Check before Load.  Nothing is written until the file has been read all
 *   the way through and come back clean. A part-loaded file is worse than no
 *   file, because nobody can tell which half went in.
 *
 *   Once only.  A material that already holds stock at that site is refused.
 *   Running the same file twice would silently double the company's stock and
 *   there is no honest way to unpick that afterwards.
 */
function OpeningStockTab() {
  const toast = useToast()
  // Names, not email addresses — management 01-09-2026.
  const people = usePeople()
  const [file, setFile] = useState<File | null>(null)
  const [busy, setBusy] = useState<'check' | 'commit' | 'template' | 'certificate' | null>(null)
  const [result, setResult] = useState<any | null>(null)
  // Sites are counted days or weeks before the sheet reaches Head Office. The
  // stock existed on the count date, so that is the date the ledger carries.
  const [asAt, setAsAt] = useState(todayIso)

  const { data: batches, refresh } = useQuery<any>('opening_stock_batches', () =>
    supabase.from('opening_stock_batches').select('*')
      .order('created_at', { ascending: false }).limit(50),
    [], { cacheable: false })

  async function post(action: 'check' | 'commit') {
    if (!file) { toast.error('Choose the completed count sheet first.'); return }
    setBusy(action)
    try {
      const buf = await file.arrayBuffer()
      // Chunked so a large sheet cannot blow the argument limit on
      // String.fromCharCode, which happens somewhere around 100k rows.
      const bytes = new Uint8Array(buf)
      let bin = ''
      for (let i = 0; i < bytes.length; i += 8192) {
        bin += String.fromCharCode(...bytes.subarray(i, i + 8192))
      }
      const res = await callFunction('opening-stock', {
        action, file_name: file.name, as_at: asAt, file_b64: btoa(bin),
      })
      const text = await res.text()
      if (!res.ok) throw new Error(text || 'The file could not be read')
      const data = JSON.parse(text)
      setResult(data)
      if (data.committed) {
        toast.success(`Loaded ${data.row_count} lines as ${data.batch_id}.`)
        setFile(null)
        refresh()
      } else if (data.ok) {
        toast.success(`${data.row_count} lines checked and clean. Press Load to write them in.`)
      } else {
        toast.error(`${data.error_count} problem${data.error_count === 1 ? '' : 's'} — nothing was loaded.`)
      }
    } catch (e) {
      toast.error(e instanceof Error ? e.message : 'Could not read the file')
      setResult(null)
    } finally { setBusy(null) }
  }

  return (
    <>
      <Card title="Load opening stock"
            subtitle="The one-time count that puts the physical stock at every site into the system">
        <Alert tone="warning" title="This is a go-live job, not a daily one">
          Opening balances are laid down <strong>once per site</strong>. After that, stock moves
          only through approved goods receipts, issues, returns and transfers. If a count turns out
          wrong later, correct it on the <strong>Adjustments</strong> tab with a reason attached —
          do not reload a file.
        </Alert>

        <ol className="mt-4 space-y-2 text-sm text-slate-700">
          <li><strong>1.</strong> Download the template and send it to every store-man.</li>
          <li><strong>2.</strong> He counts his store and fills in one row per material he holds.</li>
          <li><strong>3.</strong> You set the date he counted, upload his sheet, and press <strong>Check</strong>.</li>
          <li><strong>4.</strong> If it comes back clean, press <strong>Load</strong>.</li>
          <li><strong>5.</strong> Once <em>every</em> site is in, download the certificate below and
            have Stores and Finance sign it.</li>
        </ol>

        <div className="mt-4 flex flex-wrap items-end gap-3">
          <button className="btn-secondary" disabled={busy === 'template'}
                  onClick={async () => {
                    setBusy('template')
                    try {
                      await downloadFromFunction('opening-stock', { action: 'template' },
                        'OVAL-Opening-Stock-Template.xlsx')
                      toast.success('Template downloaded — send it to your store-men.')
                    } catch (e) {
                      toast.error(e instanceof Error ? e.message : 'Could not build the template')
                    } finally { setBusy(null) }
                  }}>
            <FileSpreadsheet className="h-4 w-4" /> Download blank template
          </button>

          <DocumentDate label="Stock counted as at" value={asAt} onChange={setAsAt}
                        hint="The day the store was physically counted, not today." />

          <Field label="Completed count sheet" className="min-w-[260px] flex-1">
            <input type="file" accept=".xlsx,.xlsm" className="field"
                   onChange={(e) => { setFile(e.target.files?.[0] ?? null); setResult(null) }} />
          </Field>

          <button className="btn-secondary" onClick={() => post('check')}
                  disabled={!file || busy !== null}>
            {busy === 'check' ? 'Checking…' : 'Check the file'}
          </button>
          <button className="btn-primary" onClick={() => post('commit')}
                  disabled={!file || busy !== null || !result?.ok || result?.committed}>
            <Upload className="h-4 w-4" /> {busy === 'commit' ? 'Loading…' : 'Load into stock'}
          </button>
        </div>

        <p className="mt-2 text-xs text-slate-500">
          The template is built fresh each time from the live material and site master, so the
          codes on it are always current.
        </p>

        {/* The certificate. Deliberately below the loader and visually separate:
            it is the LAST step, and a download button sitting beside "Load into
            stock" invites it to be pressed after the first site. */}
        <div className="mt-5 rounded-lg border border-slate-200 bg-slate-50 p-4">
          <div className="flex flex-wrap items-center justify-between gap-3">
            <div>
              <p className="text-sm font-semibold text-slate-800">
                Opening stock valuation certificate
              </p>
              <p className="mt-0.5 text-xs text-slate-600">
                The signed statement of what the opening stock is worth. Company-wide, so
                download it <strong>once, after every site is loaded</strong> — not per site.
              </p>
            </div>
            <button className="btn-secondary shrink-0" disabled={busy === 'certificate'}
                    onClick={async () => {
                      setBusy('certificate')
                      try {
                        // The valuation ledger is rebuilt first. Without it the
                        // certificate states whatever the last rebuild found,
                        // which after a fresh load is the position BEFORE that
                        // load - a stale figure that looks perfectly valid.
                        const { error: rebuildErr } = await supabase.rpc('rpc_rebuild_valuation')
                        if (rebuildErr) throw new Error(rebuildErr.message)
                        await downloadFromFunction('opening-valuation-certificate', {},
                          'Opening-Stock-Valuation-Certificate.docx')
                        toast.success('Certificate downloaded. Read the assurance line before signing.')
                      } catch (e) {
                        toast.error(e instanceof Error ? e.message : 'Could not build the certificate')
                      } finally { setBusy(null) }
                    }}>
              <FileText className="h-4 w-4" />
              {busy === 'certificate' ? 'Preparing…' : 'Download certificate (Word)'}
            </button>
          </div>
          <p className="mt-2 text-xs text-slate-500">
            Re-values every movement before building the document, so the figure is always
            current. If the certificate carries a <strong>WARNING</strong>, some line was priced
            by estimate rather than from the count — do not sign it.
          </p>
        </div>

        {result && !result.ok && (
          <div className="mt-4">
            <Alert tone="error"
                   title={`${result.error_count} problem${result.error_count === 1 ? '' : 's'} — nothing has been loaded`}>
              Fix these rows in the spreadsheet and upload it again. The row numbers count from the
              first line under the headings.
            </Alert>
            <div className="table-wrap mt-3">
              <table className="tbl">
                <thead><tr><th>Row</th><th>Site</th><th>Material</th><th>What is wrong</th></tr></thead>
                <tbody>
                  {(result.errors ?? []).map((e: any, i: number) => (
                    <tr key={i}>
                      <td className="num text-xs font-semibold">{e.line}</td>
                      <td className="font-mono text-xs">{e.site_code ?? '—'}</td>
                      <td className="font-mono text-xs">{e.material_id ?? '—'}</td>
                      <td className="text-sm text-red-700">{e.problem}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}

        {result?.ok && !result.committed && (
          <div className="mt-4">
            <Alert tone="success" title={`${result.row_count} lines checked and clean`}>
              Nothing has been written yet. Press <strong>Load into stock</strong> to post them —
              a total of {qty(result.total_quantity)} units across {result.rows_read} rows read.
            </Alert>
          </div>
        )}

        {result?.committed && (
          <div className="mt-4">
            <Alert tone="success" title={`Loaded as ${result.batch_id}`}>
              {result.row_count} material lines are now in stock. They appear on the
              <strong> Stock position</strong> tab and on the movement ledger as
              <strong> OPENING</strong> entries.
            </Alert>
          </div>
        )}
      </Card>

      <Card className="mt-4" title="Files already loaded"
            subtitle="The audit trail for how the digital stock position was first established">
        {batches.length === 0 && (
          <EmptyState icon={<Upload className="h-8 w-8" />} title="No opening stock has been loaded yet"
                      message="Start with the template above." />
        )}
        {batches.length > 0 && (
          <div className="table-wrap">
            <table className="tbl">
              <thead>
                <tr><th>Batch</th><th>File</th><th className="num">Lines</th>
                    <th className="num">Total quantity</th><th>Loaded by</th><th>Loaded at</th></tr>
              </thead>
              <tbody>
                {batches.map((b: any) => (
                  <tr key={b.id}>
                    <td className="font-mono text-xs">
                      <CheckCircle2 className="mr-1 inline h-3.5 w-3.5 text-emerald-600" />{b.id}
                    </td>
                    <td className="max-w-[240px] truncate text-xs">{b.file_name ?? '—'}</td>
                    <td className="num text-sm font-semibold">{b.row_count}</td>
                    <td className="num text-xs">{qty(b.total_lines)}</td>
                    <td className="max-w-[180px] truncate text-xs text-slate-500">{people.name({ email: b.imported_email })}</td>
                    <td className="text-xs">{dateTime(b.created_at)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </Card>
    </>
  )
}
